/**
 * The portal's half of gas sponsorship.
 *
 * Two things live here and it is worth being clear about which is which.
 *
 * The *decisions* are the server's. Which path a wallet is on, how much it is
 * short by, whether it has already been funded, whether a migration really
 * happened — none of that is computed in this file, and none of it can be
 * influenced from here. There is no request below that names a recipient or an
 * amount, because the API has nowhere to put one.
 *
 * What this file does is presentational and preparatory: it asks whether
 * sponsorship is switched on at all, it estimates locally so the card can say
 * "your gas is covered" before anybody signs anything, and it holds the session
 * that proves the wallet is the wallet.
 *
 * A failure here is never fatal. Every function returns a state the card can
 * render, and the migration itself works identically with sponsorship off —
 * which is exactly what it does today.
 */
import type { Address, EIP1193Provider, Hex } from "viem";

export type SponsorStatus = {
  enabled: boolean;
  available: boolean;
  code: string;
  /**
   * The wallet model the server funds against, published by /api/gas/metrics.
   *
   * Read rather than restated so the card cannot pad differently from the
   * backend. Falls back to the server's own defaults when the endpoint is old
   * or unreachable — the same direction every other unknown in this file takes.
   */
  walletModel: WalletModel;
};

export type WalletModel = {
  gasLimitBps: bigint;
  feeBps: bigint;
  minGasPriceWei: bigint;
  maxGasPriceWei: bigint;
};

export const DEFAULT_WALLET_MODEL: WalletModel = {
  gasLimitBps: 15_000n,
  feeBps: 20_000n,
  minGasPriceWei: 0n,
  maxGasPriceWei: 0n,
};

export type SponsorRecord = {
  id: string;
  stage: "approval" | "swap" | "reimbursement";
  status: string;
  mode: string;
  amountWei: string;
  sponsorTxHash: string;
  userTxHash: string;
  failureCode: string;
  createdAt: number;
  expiresAt: number;
};

/** Where a wallet stands against the fixed eligibility snapshot. Public — the
 *  set is a projection of chain history at a published block. */
export type SnapshotStanding = {
  wallet: string;
  eligibleForSponsoredGas: boolean;
  snapshotBlock: number;
};

export type SponsorPlan = {
  wallet: string;
  available: boolean;
  code: string;
  mode: "reimbursement" | "sponsored";
  state: string;
  inSnapshot: boolean;
  snapshotBlock: number;
  snapshotBalanceRaw: string;
  claimable: boolean;
  windowOpen: boolean;
  amountRaw: string;
  needsApproval: boolean;
  gasPriceWei: string;
  approveGas: number;
  swapGas: number;
  swapGasEstimated: boolean;
  walletBnbWei: string;
  requiredWei: string;
  deficitWei: string;
  approval: SponsorRecord | null;
  swap: SponsorRecord | null;
  reimbursement: SponsorRecord | null;
};

/** What the server said when it refused. Carries the ledger's own code so the
 *  card can distinguish "already sent" from "temporarily unavailable". */
export class SponsorRefusal extends Error {
  readonly code: string;
  readonly record: SponsorRecord | null;

  constructor(code: string, message: string, record: SponsorRecord | null) {
    super(message);
    this.name = "SponsorRefusal";
    this.code = code;
    this.record = record;
  }
}

async function readProblem(response: Response): Promise<SponsorRefusal> {
  try {
    const body = (await response.json()) as {
      error?: string;
      message?: string;
      record?: SponsorRecord;
    };
    return new SponsorRefusal(
      body.error ?? "REJECTED",
      body.message ?? "Sponsorship gas tidak tersedia saat ini.",
      body.record ?? null,
    );
  } catch {
    return new SponsorRefusal(
      "REJECTED",
      "Sponsorship gas tidak tersedia saat ini.",
      null,
    );
  }
}

/**
 * Whether this deployment sponsors anything.
 *
 * Public and aggregate — it names no wallet. Called once when the card mounts,
 * so a portal with sponsorship switched off never shows a promise it cannot
 * keep. A network failure reads as "off", which is the safe direction: the
 * fallback copy tells people to use their own BNB, and that always works.
 */
export async function fetchSponsorStatus(): Promise<SponsorStatus> {
  const unavailable: SponsorStatus = {
    enabled: false,
    available: false,
    code: "DISABLED",
    walletModel: DEFAULT_WALLET_MODEL,
  };
  try {
    const response = await fetch("/api/gas/metrics", { cache: "no-store" });
    if (!response.ok) return unavailable;
    const body = (await response.json()) as {
      enabled?: boolean;
      available?: boolean;
      code?: string;
      caps?: Record<string, string>;
    };
    return {
      enabled: Boolean(body.enabled),
      available: Boolean(body.available),
      code: body.code ?? "",
      walletModel: walletModelFrom(body.caps),
    };
  } catch {
    return unavailable;
  }
}

function bigintOr(raw: string | undefined, fallback: bigint): bigint {
  if (!raw) return fallback;
  try {
    const value = BigInt(raw);
    return value < 0n ? fallback : value;
  } catch {
    return fallback;
  }
}

export function walletModelFrom(
  caps: Record<string, string> | undefined,
): WalletModel {
  return {
    gasLimitBps: bigintOr(
      caps?.walletGasLimitBps,
      DEFAULT_WALLET_MODEL.gasLimitBps,
    ),
    feeBps: bigintOr(caps?.walletFeeBps, DEFAULT_WALLET_MODEL.feeBps),
    minGasPriceWei: bigintOr(caps?.walletMinGasPriceWei, 0n),
    maxGasPriceWei: bigintOr(caps?.maxGasPriceWei, 0n),
  };
}

/**
 * Whether this wallet is in the sponsorship eligibility set.
 *
 * Asked before anything is offered, so the card can say what is true rather
 * than "we might pay your gas". Needs no signature: the set is fixed at a block
 * this portal publishes the number and hash of, and is rebuildable by anyone.
 *
 * A failure reads as "not eligible" — the same direction every other unknown in
 * this file takes, because the fallback is the migration path that always works.
 */
export async function fetchSnapshotStanding(
  wallet: Address,
): Promise<SnapshotStanding | null> {
  try {
    const response = await fetch(`/api/gas/snapshot/${wallet}`, {
      cache: "no-store",
    });
    if (!response.ok) return null;
    return (await response.json()) as SnapshotStanding;
  } catch {
    return null;
  }
}

type Challenge = {
  wallet: string;
  nonce: string;
  digest: string;
  typedData: {
    domain: Record<string, unknown>;
    types: Record<string, { name: string; type: string }[]>;
    primaryType: string;
    message: Record<string, unknown>;
  };
};

/**
 * Sessions, kept in memory for as long as the tab lives.
 *
 * Not in localStorage. A session token is the thing that says "this wallet
 * proved itself", and one that survives the tab would outlive the wallet
 * connection it was granted against — a different wallet connected later in the
 * same browser would find a token waiting for it. Re-signing costs one wallet
 * prompt and removes the whole question.
 */
const sessions = new Map<string, { token: string; expiresAt: number }>();

function cachedSession(wallet: Address): string | null {
  const held = sessions.get(wallet.toLowerCase());
  if (!held) return null;
  // A minute of headroom: a token that expires mid-request is a failure the
  // user cannot act on.
  if (held.expiresAt - 60_000 <= Date.now()) {
    sessions.delete(wallet.toLowerCase());
    return null;
  }
  return held.token;
}

export function forgetSession(wallet: Address): void {
  sessions.delete(wallet.toLowerCase());
}

/**
 * Proves the wallet, and returns a bearer token for the routes that spend.
 *
 * The typed data is passed to the wallet exactly as the server sent it, through
 * `eth_signTypedData_v4` rather than through a typed helper. That is the point:
 * a message reassembled in the browser would be a second definition of what the
 * user is signing, and the server verifies against its own. One definition,
 * serialised once, signed as-is.
 */
export async function openSession(
  wallet: Address,
  provider: EIP1193Provider,
): Promise<string> {
  const existing = cachedSession(wallet);
  if (existing) return existing;

  const challengeResponse = await fetch(`/api/gas/challenge/${wallet}`, {
    cache: "no-store",
  });
  if (!challengeResponse.ok) throw await readProblem(challengeResponse);
  const challenge = (await challengeResponse.json()) as Challenge;

  const signature = (await provider.request({
    method: "eth_signTypedData_v4",
    params: [wallet, JSON.stringify(challenge.typedData)],
  } as never)) as Hex;

  const sessionResponse = await fetch("/api/gas/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ wallet, nonce: challenge.nonce, signature }),
  });
  if (!sessionResponse.ok) throw await readProblem(sessionResponse);

  const body = (await sessionResponse.json()) as {
    token: string;
    ttlSeconds: number;
  };
  sessions.set(wallet.toLowerCase(), {
    token: body.token,
    expiresAt: Date.now() + body.ttlSeconds * 1_000,
  });
  return body.token;
}

async function authed<T>(
  token: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    cache: "no-store",
    headers: {
      authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw await readProblem(response);
  return (await response.json()) as T;
}

/** The server's own view of where this wallet stands. Authoritative; the local
 *  estimate below is only for what to show before a session exists. */
export function fetchPlan(token: string): Promise<SponsorPlan> {
  return authed<SponsorPlan>(token, "/api/gas/plan");
}

/**
 * Asks for the next stage to be funded.
 *
 * Note the argument list: a token, and nothing else. Which stage, how much, and
 * to whom are all the server's, read from the chain at the moment it decides.
 */
export function requestSponsorship(token: string): Promise<SponsorRecord> {
  return authed<SponsorRecord>(token, "/api/gas/sponsor", {});
}

/**
 * Asks for a completed transaction's gas to be paid back.
 *
 * The hash is the only input, and it is not trusted: the server fetches the
 * transaction and its receipt, checks the sender, the destination, the calldata
 * and the emitted event, and pays from what it read rather than from what was
 * claimed.
 */
export function requestReimbursement(
  token: string,
  txHash: Hex,
): Promise<SponsorRecord> {
  return authed<SponsorRecord>(token, "/api/gas/reimburse", { txHash });
}

/**
 * What this wallet must hold before the transaction is worth offering.
 *
 * The larger of two figures, and the second one is the one that matters. A
 * wallet does not send the transaction we estimated: MetaMask pads the gas limit
 * with a buffer of its own, prices it with a fee it derives itself, and greys
 * out the confirm button unless the balance covers the product. Comparing
 * against the execution cost alone tells a zero-BNB user "gas sudah tersedia"
 * and then hands the wallet a transaction it will not let them sign — which is
 * exactly what happened on chain 97 on 2026-08-28.
 *
 * The model comes from the server (`/api/gas/metrics`) rather than being
 * restated here, so the card and the backend cannot drift apart. The server
 * re-derives all of it before any BNB moves, and its answer wins.
 */
export function requiredBalanceWei(
  gasEstimate: bigint,
  gasPriceWei: bigint,
  model: WalletModel = DEFAULT_WALLET_MODEL,
): bigint {
  const execution = (gasEstimate + (gasEstimate * 2_500n) / 10_000n) * gasPriceWei;

  const gasLimitBps =
    model.gasLimitBps < 10_000n ? 10_000n : model.gasLimitBps;
  const feeBps = model.feeBps < 10_000n ? 10_000n : model.feeBps;

  let fee = (gasPriceWei * feeBps) / 10_000n;
  if (model.minGasPriceWei > 0n && fee < model.minGasPriceWei) {
    fee = model.minGasPriceWei;
  }
  // Clamped at the price above which sponsorship refuses to pay at all, which
  // is what keeps the figure inside the per-stage caps.
  if (model.maxGasPriceWei > 0n && fee > model.maxGasPriceWei) {
    fee = model.maxGasPriceWei;
  }

  const wallet = ((gasEstimate * gasLimitBps) / 10_000n) * fee;
  return wallet > execution ? wallet : execution;
}

/**
 * A local estimate of whether this wallet can pay its own way.
 *
 * Used only to choose what the card offers before anybody signs anything.
 */
/**
 * The gas limit to put on the transaction, rather than leaving it to the wallet.
 *
 * Left unset, a wallet picks its own limit and its own price, and on chain 97
 * that is not one number but several: nodes quote gas 0,1–1 gwei for the same
 * block, `eth_maxPriorityFeePerGas` is unsupported on some of them, and the
 * fee-history percentiles a wallet falls back to disagree with the price it
 * would otherwise use. The portal cannot fund a figure it cannot predict.
 *
 * Stating the limit — the same buffer the server funded against — makes the
 * wallet's arithmetic ours: max cost becomes exactly `gasLimit × gasPrice`,
 * both of them numbers the sponsorship already covered.
 */
export function walletGasLimit(
  gasEstimate: bigint,
  model: WalletModel = DEFAULT_WALLET_MODEL,
): bigint {
  const bps = model.gasLimitBps < 10_000n ? 10_000n : model.gasLimitBps;
  return (gasEstimate * bps) / 10_000n;
}

export function coversItsOwnGas(
  balanceWei: bigint,
  gasEstimate: bigint,
  gasPriceWei: bigint,
  model: WalletModel = DEFAULT_WALLET_MODEL,
): boolean {
  return balanceWei >= requiredBalanceWei(gasEstimate, gasPriceWei, model);
}

/** Human-readable reasons, for the states the card has to explain rather than
 *  merely disable. Anything not listed falls back to the server's message. */
export const SPONSOR_MESSAGES: Record<string, string> = {
  DISABLED: "Sponsorship gas sedang tidak aktif.",
  BUDGET_EXCEEDED: "Kuota sponsorship gas untuk periode ini sudah habis.",
  SPONSOR_BALANCE: "Sponsorship gas sedang tidak tersedia.",
  GAS_PRICE_ABNORMAL:
    "Biaya jaringan sedang tidak normal, sponsorship dijeda sementara.",
  NOT_ALLOWLISTED: "Sponsorship gas sedang dibuka terbatas.",
  ALREADY_SPONSORED: "Gas untuk tahap ini sudah dikirim ke wallet Anda.",
  /* Both say the same thing to the reader, and both lead with the part that
     matters: the migration is unaffected. Nothing here may read as "you cannot
     migrate" — that decision belongs to AsetquSwap and this is not it. */
  NOT_IN_SNAPSHOT:
    "Anda tetap dapat bermigrasi seperti biasa, tetapi gas yang ditanggung " +
    "perusahaan tidak tersedia untuk wallet ini.",
  BELOW_DUST_FLOOR:
    "Anda tetap dapat bermigrasi seperti biasa, tetapi gas yang ditanggung " +
    "perusahaan tidak tersedia untuk wallet ini.",
  SELF_FUNDED:
    "Saldo BNB Anda sudah cukup. Biaya gas akan diganti setelah migrasi berhasil.",
  WALLET_IS_CONTRACT:
    "Sponsorship gas hanya untuk wallet biasa, bukan kontrak.",
};
